import isFungible from '@/utils/is-fungible'
import { collectionObjectPagePath } from '@/utils/collection-object'

const VIRTUAL_SLUGS = new Set(['frozen', 'uncategorized'])

function unique(values: Array<string | null | undefined> = []): string[] {
	const seen = new Set<string>()
	const result: string[] = []
	for (const value of values) {
		if (!value || seen.has(value)) continue
		seen.add(value)
		result.push(value)
	}
	return result
}

/**
 * Where a successful listing toast should open.
 * A single listed lot opens that inscription. Bulk fungible lots
 * (TAP/DMT/runes/alkanes) and bitmaps land on the collection page — bitmap's
 * for-sale tab is the default there. Bulk ordinals open the shared market.
 */
export function listingSuccessHref(input: {
	slug?: string | null
	inscriptionIds?: Array<string | null | undefined>
	collectionSlugs?: Array<string | null | undefined>
}): string | null {
	const slug = input.slug?.trim() || ''
	const inscriptionIds = unique(input.inscriptionIds)
	const collectionSlugs = unique(input.collectionSlugs).filter((value) => !VIRTUAL_SLUGS.has(value))
	const bitmapOnly =
		slug === 'bitmap' ||
		(!slug && collectionSlugs.length > 0 && collectionSlugs.every((value) => value === 'bitmap'))

	if (bitmapOnly) {
		return '/collection/bitmap'
	}

	// One lot, one destination: the listing itself, even on a fungible market.
	if (inscriptionIds.length === 1) {
		return `/inscription/${encodeURIComponent(inscriptionIds[0])}`
	}

	if (slug && isFungible(slug)) {
		return collectionObjectPagePath(slug)
	}

	if (slug && !VIRTUAL_SLUGS.has(slug)) {
		return collectionObjectPagePath(slug)
	}

	if (collectionSlugs.length === 1) {
		return collectionSlugs[0] === 'bitmap'
			? '/collection/bitmap'
			: collectionObjectPagePath(collectionSlugs[0])
	}

	if (inscriptionIds.length > 1) {
		return `/inscription/${encodeURIComponent(inscriptionIds[0])}`
	}

	return null
}

export function listingSuccessActionLabel(href: string, count: number): string {
	if (href.startsWith('/inscription/') || count <= 1) {
		return 'View listing'
	}
	return 'View listings'
}

export function listingSuccessTarget(input: {
	slug?: string | null
	inscriptionIds?: Array<string | null | undefined>
	collectionSlugs?: Array<string | null | undefined>
	count: number
}): { href: string; label: string } | null {
	if (input.count <= 0) return null
	const href = listingSuccessHref(input)
	if (!href) return null
	return { href, label: listingSuccessActionLabel(href, input.count) }
}
